import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { apiFetch } from '../utils/apiFetch';
import { API_BASE_URL } from '../utils/apiBaseUrl';

const passwordRequirements = [
  { label: 'At least 8 characters', test: (pw: string) => pw.length >= 8 },
  { label: 'One uppercase letter', test: (pw: string) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw: string) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw: string) => /[0-9]/.test(pw) },
  { label: 'One special character', test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
];

function passwordIsStrong(pw: string) {
  return passwordRequirements.every((req) => req.test(pw));
}

function authHeaders(): HeadersInit | undefined {
  const token = localStorage.getItem('lts_jwt_token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

const MustChangePasswordGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState<'loading' | 'required' | 'ok' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [checkId, setCheckId] = useState(0);

  useEffect(() => {
    if (!user || user.isKiosk) {
      setPhase('ok');
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setPhase('loading');
    setLoadError(null);
    (async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/auth/me`, { headers: authHeaders() });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setLoadError(data?.message || 'Could not verify whether a new password is required.');
          setPhase('error');
          return;
        }
        setPhase(data?.user?.mustChangePassword ? 'required' : 'ok');
      } catch {
        if (!cancelled) {
          setLoadError('Could not verify whether a new password is required.');
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?._id, user?.isKiosk, checkId]);

  if (!user || user.isKiosk || phase === 'ok') return <>{children}</>;

  if (phase === 'loading') {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="flex justify-center items-center min-h-screen p-4">
        <div className="alert alert-error max-w-md flex-col items-start gap-3">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm" onClick={() => setCheckId((n) => n + 1)}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return <MustChangePasswordModal onDone={() => setPhase('ok')} />;
};

const MustChangePasswordModal: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const strong = passwordIsStrong(newPassword);
  const matches = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = strong && matches && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await userService.completeRequiredPasswordChange(newPassword, confirmPassword);
      onDone();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password.';
      if (/not required/i.test(message)) {
        onDone();
        return;
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal modal-open z-[80]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="must-change-password-title"
    >
      <div className="modal-box max-w-md">
        <h2 id="must-change-password-title" className="font-bold text-xl">Choose a new password</h2>
        <p className="text-sm text-base-content/70 mt-1 mb-4">
          Your temporary password was only for signing in. Set a new password to continue.
        </p>
        {error && (
          <div className="alert alert-error text-sm mb-3">
            <span>{error}</span>
          </div>
        )}
        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <div>
            <label className="label py-1" htmlFor="required-new-password">
              <span className="label-text font-medium">New password</span>
            </label>
            <div className="join w-full">
              <input
                id="required-new-password"
                type={showNew ? 'text' : 'password'}
                className="input input-bordered join-item w-full"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                autoFocus
                required
              />
              <button
                type="button"
                className="btn btn-ghost join-item"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
              </button>
            </div>
            {newPassword && (
              <ul className="mt-2 text-xs space-y-1">
                {passwordRequirements.map((req) => (
                  <li key={req.label} className={req.test(newPassword) ? 'text-success' : 'text-error'}>
                    {req.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="label py-1" htmlFor="required-confirm-password">
              <span className="label-text font-medium">Confirm password</span>
            </label>
            <div className="join w-full">
              <input
                id="required-confirm-password"
                type={showConfirm ? 'text' : 'password'}
                className="input input-bordered join-item w-full"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="btn btn-ghost join-item"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <div className="text-error text-xs mt-1">Passwords do not match</div>
            )}
          </div>
          <button className="btn btn-primary mt-2" type="submit" disabled={!canSubmit}>
            {submitting ? 'Saving...' : 'Save password'}
          </button>
        </form>
      </div>
      <div className="modal-backdrop" />
    </div>
  );
};

export default MustChangePasswordGate;
